<template>
  <div id="import-hike-dialog-backdrop" class="modal fade" tabindex="-1" role="dialog" aria-hidden="true">
    <div class="modal-dialog modal-md modal-dialog-centered" role="document">
      <div class="modal-content">
        <div class="modal-body p-5">
          <div class="import-dialog">
            <header>
              <h3><strong>Import Hikes</strong></h3>
            </header>
            <div class="file-drop-area rounded-lg p-3 d-flex flex-column align-items-center"
                 @drop.prevent="handleFileDrop"
                 @dragover.prevent>
              <p class="mb-3">Drag & Drop GPX or FIT files here, or click to select.</p>
              <input ref="fileInput"
                     type="file"
                     @change="handleFileChange"
                     multiple
                     accept=".gpx,.fit"
                     hidden>
              <button class="browse-button btn btn-sm btn-secondary" @click="triggerFileInput">Choose Files</button>
            </div>
            <ul class="file-list mt-3">
              <li v-for="(file, index) in files" :key="index" class="file-item">
                <span class="file-name">{{ file.name }}</span>
                <button class="remove-btn" @click="removeFile(index)" title="Remove">&times;</button>
              </li>
            </ul>
            <!-- Single Import Button -->
            <div class="w-100 d-flex justify-content-center mt-3">
              <button class="btn btn-sm btn-primary" v-if="files.length > 0 && !isImporting"
                      @click="importAllFiles">
                Import Files
              </button>
              <div v-else-if="isImporting" class="loading">Importing...</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import GpxParser from "gpxparser";
import FitParser from 'fit-file-parser';

// gpxparser's queryDirectSelector uses undeclared `idx`/`elem` globals, which
// ReferenceError under strict mode (ESM). It's only hit when a parent element
// has >1 descendants matching a tag — e.g. metadata containing both
// <author><link/></author> and a top-level <link/> (common in Strava exports).
if (!(GpxParser as any).__queryDirectSelectorPatched) {
  (GpxParser as any).prototype.queryDirectSelector = function (node: Element, tagName: string) {
    const nodes = node.querySelectorAll(tagName);
    let trueNode: Element | undefined = nodes[0];
    if (nodes.length > 1) {
      const children = node.childNodes;
      for (let i = 0; i < children.length; i++) {
        const child = children[i] as Element;
        if (child && child.tagName === tagName) {
          trueNode = child;
        }
      }
    }
    return trueNode;
  };
  (GpxParser as any).__queryDirectSelectorPatched = true;
}
import graph_client from "../../utils/graphql/graph_client";
import { CreateHikeWithPlannedTrails } from "../../utils/graphql/hikes-graph";
import { Modal } from "bootstrap";
import distance from '@turf/distance';
import simplify from '@turf/simplify';
import { getElevationData } from "../../utils/helpers/HiikerElevationApiHelper";

export default {
  data() {
    return {
      files: [],
      isImporting: false,
    };
  },
  methods: {
    triggerFileInput() {
      this.$refs.fileInput.click();
    },
    removeFile(index) {
      this.files.splice(index, 1);
    },
    handleFileChange(event) {
      const filesAdded = Array.from(event.target.files);
      this.files.push(...filesAdded.map(file => ({
        name: file.name,
        file: file,
      })));
    },
    handleFileDrop(event) {
      const filesAdded = Array.from(event.dataTransfer.files);
      this.files.push(...filesAdded.map(file => ({
        name: file.name,
        file: file,
      })));
    },
    async importAllFiles() {
      if (this.isImporting) return;
      this.isImporting = true;

      try {
        for (const fileEntry of this.files) {
          await this.importFile(fileEntry);
        }

        // Reload the hikes list
        window.location.href = '/user/hikes';
      } catch (error) {
        console.error('Import error:', error);
        alert(`Failed to import files: ${error.message}`);
      } finally {
        this.isImporting = false;
      }
    },
    async importFile(fileEntry) {
      try {
        const file = fileEntry.file;
        const title = file.name.replace(/\.[^/.]+$/, '');
        const extension = file.name.split('.').pop().toLowerCase();
        let trackData;

        if (extension === 'gpx') {
          trackData = await this.parseGpxFile(file, title);
        } else if (extension === 'fit') {
          trackData = await this.parseFitFile(file, title);
        } else {
          throw new Error('Unsupported file type');
        }

        // Fetch corrected elevation data from the elevation API
        const coordsForElevationAPI = trackData.coordinates.map(coord => [coord[1], coord[0]]); // [lat, lng]
        const elevationData = await getElevationData(coordsForElevationAPI);

        if (elevationData && elevationData.length === trackData.coordinates.length) {
          // Update coordinates with corrected elevation
          trackData.coordinates = elevationData.map(e => [e.lng, e.lat, e.elevation]);
          trackData.altitudes = elevationData.map(e => e.elevation);
        }

        // Recalculate distances with the (possibly updated) coordinates
        trackData.distances = this.calculateDistances(trackData.coordinates);

        // Calculate basic statistics with corrected elevation
        const distanceMeters = trackData.distances[trackData.distances.length - 1] || 0;
        const elevationGain = this.calculateElevationGain(trackData.altitudes);
        const elevationLoss = this.calculateElevationLoss(trackData.altitudes);

        // Create markers for start and end points
        const firstPoint = trackData.coordinates[0];
        const lastPoint = trackData.coordinates[trackData.coordinates.length - 1];

        const markerAttributes = [
          {
            elevationMeters: Math.round(firstPoint[2] || 0),
            lonlat: `POINT(${firstPoint[0]} ${firstPoint[1]})`,
            snap: false
          },
          {
            elevationMeters: Math.round(lastPoint[2] || 0),
            lonlat: `POINT(${lastPoint[0]} ${lastPoint[1]})`,
            snap: false
          }
        ];

        // Create linestring in WKT format with distances
        const lineStringWKT = this.createLineStringWKT(trackData.coordinates, trackData.distances);

        const lineStringAttributes = [{
          geometry: lineStringWKT,
          surfaces: new Array(trackData.coordinates.length).fill(0), // Unknown surface
          distances: trackData.distances
        }];

        const variables = {
          input: {
            name: title,
            description: `Imported from ${file.name}`,
            publiclyVisible: false,
            difficulty: 'Medium',
            distanceMeters: Math.round(distanceMeters),
            elevationGainMeters: Math.round(elevationGain),
            elevationLossMeters: Math.round(elevationLoss),
            plannedTrailsAttributes: [{
              title: title,
              markersAttributes: markerAttributes,
              lineStringsAttributes: lineStringAttributes,
              position: 1,
              public: false,
              routeStyle: 'route',
              day: 1
            }]
          }
        };

        const response = await graph_client.request(CreateHikeWithPlannedTrails, variables);

        if (response.createHike?.errors?.length > 0) {
          const errors = response.createHike.errors.map((e: any) =>
            `${e.fieldName}: ${e.errors.join(', ')}`
          ).join('\n');
          throw new Error(`Errors creating hike:\n${errors}`);
        }

        if (!response.createHike?.hike?.id) {
          throw new Error('Unknown error occurred while creating hike');
        }
      } catch (error) {
        console.error('Import error:', error);
        throw error;
      }
    },
    async parseGpxFile(file, title) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = async (e) => {
          try {
            let parsed = new GpxParser();
            parsed.parse(e.target.result);

            const hasTrack = parsed.tracks && parsed.tracks.length > 0;
            const hasRoute = parsed.routes && parsed.routes.length > 0;

            if (!hasTrack && !hasRoute) {
              throw new Error('GPX file must contain at least one track or route');
            }

            const source = hasTrack ? parsed.tracks[0] : parsed.routes[0];
            let points = source.points.map(p => ({
              lat: p.lat,
              lon: p.lon,
              elevation: p.ele || 0,
            }));

            // Simplify large tracks to improve performance
            // Only simplify if there are more than 500 points
            if (points.length > 500) {
              console.log(`Simplifying track with ${points.length} points...`);

              // Convert points to GeoJSON LineString for simplification
              const lineString = {
                type: 'Feature',
                properties: {},
                geometry: {
                  type: 'LineString',
                  coordinates: points.map(p => [p.lon, p.lat, p.elevation])
                }
              };

              // Simplify with tolerance (higher = more aggressive simplification)
              // 0.0001 degrees ≈ 11 meters, good balance between detail and performance
              const simplified = simplify(lineString, {tolerance: 0.0001, highQuality: true});

              points = simplified.geometry.coordinates.map(coord => ({
                lon: coord[0],
                lat: coord[1],
                elevation: coord[2] || 0
              }));

              console.log(`Simplified to ${points.length} points`);
            }

            if (parsed.metadata && parsed.metadata.name) {
              title = parsed.metadata.name;
            }
            title = this.stripEmoji(title);

            resolve(this.processTrackData(points, title));
          } catch (error) {
            reject(error);
          }
        };
        reader.readAsText(file);
      });
    },
    async parseFitFile(file, title) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            new FitParser({
              force: true,
              speedUnit: 'm/s',
            }).parse(e.target.result, (error, data) => {
              if (error) return reject(error);

              const points = data.records
                .filter(r => r.position_lat && r.position_long)
                .map(r => ({
                  lat: r.position_lat,
                  lon: r.position_long,
                  elevation: r.altitude || 0,
                }));

              resolve(this.processTrackData(points, title));
            });
          } catch (error) {
            reject(error);
          }
        };
        reader.readAsArrayBuffer(file);
      });
    },
    processTrackData(points, title) {
      const coordinates = [];
      const altitudes = [];

      points.forEach((p) => {
        coordinates.push([p.lon, p.lat, p.elevation || 0]);
        altitudes.push(p.elevation || 0);
      });

      const distances = this.calculateDistances(coordinates);

      return {
        title,
        coordinates,
        distances,
        altitudes
      };
    },
    calculateDistances(coordinates) {
      let cumulativeDistance = 0;
      const distances = [0];

      for (let i = 1; i < coordinates.length; i++) {
        const segmentDistance = distance(
          [coordinates[i - 1][0], coordinates[i - 1][1]],
          [coordinates[i][0], coordinates[i][1]],
          { units: 'meters' }
        );
        cumulativeDistance += segmentDistance;
        distances.push(Math.round(cumulativeDistance * 10) / 10);
      }

      return distances;
    },
    stripEmoji(str) {
      if (!str) return str;
      return str
        .replace(/\p{Extended_Pictographic}/gu, '')
        .replace(/\s+/g, ' ')
        .trim();
    },
    createLineStringWKT(coordinates, distances) {
      const points = coordinates.map((coord, i) => {
        const lon = coord[0];
        const lat = coord[1];
        const ele = coord[2] || 0;
        const dist = distances[i] || 0;
        return `${lon} ${lat} ${ele} ${dist}`;
      }).join(', ');

      return `LINESTRING (${points})`;
    },
    calculateElevationGain(altitudes) {
      let gain = 0;
      for (let i = 1; i < altitudes.length; i++) {
        const diff = altitudes[i] - altitudes[i - 1];
        if (diff > 0) {
          gain += diff;
        }
      }
      return gain;
    },
    calculateElevationLoss(altitudes) {
      let loss = 0;
      for (let i = 1; i < altitudes.length; i++) {
        const diff = altitudes[i] - altitudes[i - 1];
        if (diff < 0) {
          loss += Math.abs(diff);
        }
      }
      return loss;
    },
  },
};
</script>

<style scoped>
.file-drop-area {
  border: 2px dashed var(--gray-border);
  padding: 10px;
  text-align: center;
  margin: 20px 0;
}

.file-list {
  list-style-type: none;
  padding: 0;
  margin: 0;
}

.file-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 10px;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.08);
  margin-bottom: 6px;
  gap: 10px;
}

.file-name {
  font-size: 0.875rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
}

.remove-btn {
  background: none;
  border: none;
  color: var(--bs-secondary, #6c757d);
  font-size: 1.2rem;
  line-height: 1;
  padding: 0 2px;
  flex-shrink: 0;
  opacity: 0.6;
}

.remove-btn:hover {
  opacity: 1;
  color: var(--bs-danger, #dc3545);
}

button {
  cursor: pointer;
}

.loading {
  color: #555;
}
</style>
